import { useState } from 'react';
import { useSettings, ProfileSettings } from '@/contexts/SettingsContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader, AlertCircle, Check, X } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { PhoneInput } from '@/components/ui/phone-input';
import { isValidPhoneNumber } from 'react-phone-number-input';

export function ProfileSettingsSection() {
  const { 
    profile, 
    isUpdating, 
    updateProfile, 
    verifyEmailChange, 
    resendEmailVerification, 
    cancelEmailChange, 
    pendingEmailChange, 
    pendingEmail 
  } = useSettings();
  
  const [formData, setFormData] = useState<ProfileSettings>({
    name: profile.name,
    email: profile.email,
    whatsappNumber: profile.whatsappNumber,
  });
  
  const [otp, setOtp] = useState('');
  const [phoneError, setPhoneError] = useState('');
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate phone number
    if (formData.whatsappNumber && !isValidPhoneNumber(formData.whatsappNumber)) {
      setPhoneError('Please enter a valid phone number');
      return;
    }
    
    try {
      await updateProfile(formData);
    } catch (error) {
      console.error('Error updating profile:', error);
    }
  };
  
  const handleVerifySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.length === 6) {
      await verifyEmailChange(otp);
      setOtp('');
    }
  };
  
  const handleResendCode = async () => {
    await resendEmailVerification();
  };
  
  const handleCancelEmailChange = async () => {
    await cancelEmailChange();
    setOtp('');
  };
  
  return (
    <div>
        {pendingEmailChange && (
          <div className="mb-6">
            <Alert className="mb-4 bg-yellow-50 border-yellow-200">
              <AlertCircle className="h-4 w-4 text-yellow-600" />
              <AlertDescription>
                We've sent a verification code to <strong>{pendingEmail}</strong>. 
                Please enter the 6-digit code to verify your new email address.
              </AlertDescription>
            </Alert>
            
            <form onSubmit={handleVerifySubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="otp">Verification Code</Label>
                <div className="flex gap-2">
                  <Input
                    id="otp"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    placeholder="Enter 6-digit code"
                    maxLength={6}
                    className="flex-1"
                  />
                  <Button type="submit" disabled={otp.length !== 6 || isUpdating}>
                    {isUpdating ? (
                      <>
                        <Loader className="mr-2 h-4 w-4 animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      <>
                        <Check className="mr-2 h-4 w-4" />
                        Verify
                      </>
                    )}
                  </Button>
                </div>
              </div>
              
              <div className="flex gap-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={handleResendCode}
                  disabled={isUpdating}
                >
                  Resend Code
                </Button>
                
                <Button 
                  type="button" 
                  variant="destructive" 
                  onClick={handleCancelEmailChange}
                  disabled={isUpdating}
                >
                  <X className="mr-2 h-4 w-4" />
                  Cancel Change
                </Button>
              </div>
            </form>
            
            <div className="my-4 border-t" />
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Full Name</Label>
            <Input
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Your full name"
              className="!h-9 border-gray-300 text-[12px] font-light"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="your@email.com"
              disabled={pendingEmailChange}
              className="!h-9 border-gray-300 text-[12px] font-light"
            />
            <p className="text-[10px] text-muted-foreground">
              Changing your email will require verification
            </p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="whatsappNumber">WhatsApp Number</Label>
            <PhoneInput
              international
              defaultCountry="NG"
              value={formData.whatsappNumber}
              onChange={(value) => {
                setFormData(prev => ({ ...prev, whatsappNumber: value || '' }));
                setPhoneError('');
              }}
              placeholder="Enter your WhatsApp number"
              error={phoneError}
              className="!h-9 border-gray-300 text-[12px] font-light"
            />
            {phoneError && (
              <p className="text-[10px] text-destructive">{phoneError}</p>
            )}
          </div>
          <div className="flex justify-end"> 
          <Button type="submit" size="sm" disabled={isUpdating || pendingEmailChange}>
            {isUpdating && !pendingEmailChange ? (
              <>
                <Loader className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </form>
      </div>
  );
} 